import { officeExtentions } from "../../../src/taskpane/config/constants";
import { FilterItem, FilterSection } from "../../../src/taskpane/redux/models/reduxModels";
import { ParsedPreselectFilters } from "./preselectFilterParser";
import { buildOfficeBucketLookup, DOCICON_FIELD_NAME, findSectionByKey, isDateTimeField, normalizeExtension } from "./preselectFieldMatching";
import { SynthesizeContext, synthesizePreselectSections } from "./preselectFilterSynthesizer";

// Inputs for the build-loss fallback: the same schema fields + context the fast
// path synthesized from, so a re-synthesized section is identical by construction
// (same function, same inputs) and its signature matches the fast one.
export interface PreselectSectionFallback {
  schemaFields: any[];
  ctx: SynthesizeContext;
}

export interface PreselectApplyResult {
  sections: FilterSection[];
  appliedCount: number;
  injectedCount: number;
  dropped: string[];
}

const OFFICE_BUCKET_LOOKUP = buildOfficeBucketLookup(officeExtentions);

const itemMatchesValue = (section: FilterSection, item: FilterItem, value: string): boolean => {
  const target = value.trim().toLowerCase();
  if (section.filterField?.RealFieldName === DOCICON_FIELD_NAME) {
    const extension = normalizeExtension(value);
    const normalized = OFFICE_BUCKET_LOOKUP[extension]?.fileType ?? extension;
    return item.fileType?.toLowerCase() === normalized;
  }
  return item.title?.toLowerCase() === target || item.value?.toLowerCase() === target;
};

export const applyPreselectFilters = (
  sections: FilterSection[],
  parsed: ParsedPreselectFilters,
  fallback?: PreselectSectionFallback,
): PreselectApplyResult => {
  // A TOTAL build failure (setFilterSectionsFromFields' outer catch) yields a
  // nullish section list. That is exactly the failure mode the build-loss
  // fallback below exists for, so it must reach the fallback as "every field
  // lost" — not crash here and abandon the deep link.
  const builtSections = sections ?? [];
  const dropped: string[] = [];
  const selectedTitlesBySection = new Map<FilterSection, Set<string>>();
  // Values requested for a resolved section that matched no built item. They are
  // retained as injected items (kept applied) — NOT discarded — so the panel can
  // surface them and the existence-probe pass can decide whether to strike them
  // through. Only populated for sections that were found: an unknown or DateTime
  // key creates nothing anywhere, while a known field whose section the build
  // LOST is recovered wholesale by the fallback at the end instead.
  const unmatchedBySection = new Map<FilterSection, string[]>();
  let appliedCount = 0;

  for (const field of parsed.fields) {
    const section = findSectionByKey(builtSections, field.key);

    if (!section || isDateTimeField(section.filterField)) {
      dropped.push(field.key);
      continue;
    }

    const selectedTitles = selectedTitlesBySection.get(section) ?? new Set<string>();
    const unmatched = unmatchedBySection.get(section) ?? [];

    // Datasource-mutated (multiSelect-bound Text) sections hold SPLIT parts: the
    // build splits comma-joined values (FileFilterHelper) and both synthesizer
    // call sites split the querystring value the same way. Matching the joint
    // value here would miss every part, break fast-vs-reconciled parity on the
    // build-SUCCESS path, and let the "corrective" re-fetch replace the split
    // Contains(a) OR Contains(b) query with a single joint Contains("a, b").
    const values = section.datasourceMutated
      ? field.values.reduce<string[]>((parts, value) => parts.concat(value.split(",").map(part => part.trim()).filter(Boolean)), [])
      : field.values;

    for (const value of values) {
      const matchedItem = section.items.find(item => itemMatchesValue(section, item, value));
      if (matchedItem) {
        selectedTitles.add(matchedItem.title);
        appliedCount += 1;
      } else {
        dropped.push(`${field.key}:${value}`);
        unmatched.push(value);
      }
    }

    selectedTitlesBySection.set(section, selectedTitles);
    unmatchedBySection.set(section, unmatched);
  }

  let injectedCount = 0;
  const nextSections = builtSections.map(section => {
    const selectedTitles = selectedTitlesBySection.get(section);
    const unmatched = unmatchedBySection.get(section) ?? [];
    if ((!selectedTitles || selectedTitles.size === 0) && unmatched.length === 0) {
      return section;
    }

    const updated = section.items.map(item =>
      selectedTitles?.has(item.title) ? { ...item, selected: true } : item,
    );

    // Retain each unmatched value as a selected, injected item (deduped against
    // existing item titles case-insensitively so we never create a duplicate row).
    const existingTitles = new Set(section.items.map(i => i.title?.toLowerCase()));
    const isDocIcon = section.filterField?.RealFieldName === DOCICON_FIELD_NAME;
    const injectedFileTypes = new Set(section.items.map(i => i.fileType?.toLowerCase()).filter(Boolean));
    const injected: FilterItem[] = [];
    for (const value of unmatched) {
      // An unmatched OFFICE-family extension must be injected as the same bucket
      // item the fast path synthesized (encoded multi-extension value + fileType),
      // or fast-vs-reconciled signature parity is structurally impossible and the
      // corrective re-fetch fires on every navigation — with a narrower literal
      // filter. Non-office extensions keep the raw form: its value equals the fast
      // synthesized value (parity holds) and it stays probe-able for strike-through.
      if (isDocIcon) {
        const extension = normalizeExtension(value);
        // "?filetype=." normalizes to "" — skip it (the fast path skips it too,
        // so parity holds) instead of minting an empty, selected filter row.
        if (!extension) {
          continue;
        }
        const bucket = OFFICE_BUCKET_LOOKUP[extension];
        if (bucket) {
          if (injectedFileTypes.has(bucket.fileType)) {
            continue;
          }
          injectedFileTypes.add(bucket.fileType);
          // Panel item toggles match by TITLE, so a later raw value equal to the
          // bucket title (e.g. ?filetype=doc;Word) must dedupe against this row.
          existingTitles.add(bucket.title.toLowerCase());
          injectedCount += 1;
          injected.push({ title: bucket.title, value: bucket.values, fileType: bucket.fileType, selected: true, preselectInjected: true });
          continue;
        }
      }
      // A raw DocIcon injection must carry the NORMALIZED extension — the fast
      // path synthesizes "pdf" from "?filetype=.PDF", so injecting the verbatim
      // value would make the signatures differ on every navigation (perpetual
      // corrective re-fetch) and filter fetch #2 on the literal ".PDF".
      const injectedValue = isDocIcon ? normalizeExtension(value) : value;
      if (existingTitles.has(injectedValue.toLowerCase())) {
        continue;
      }
      existingTitles.add(injectedValue.toLowerCase());
      injectedCount += 1;
      injected.push({ title: injectedValue, value: injectedValue, selected: true, preselectInjected: true });
    }

    return { ...section, items: [...updated, ...injected] };
  });

  // Build-loss fallback: a parsed field with NO built section at all (its
  // RenderListFilterData call failed — value-list pruning is already exempted
  // upstream via keepFieldKeys) would otherwise lose its applied filter: the
  // fast-vs-reconciled signature comparison reads the loss as a query change and
  // fires a corrective re-fetch whose sections carry no selection — a second,
  // unfiltered load. Re-synthesize the section exactly like the fast path with
  // every value marked injected, so Reset strips the whole section and the
  // existence probes decide strike-through. Unknown and DateTime fields stay
  // dropped — the synthesizer skips them the same way this resolver does.
  const fallbackSections: FilterSection[] = [];
  if (fallback) {
    const lostFields = parsed.fields.filter(field => !findSectionByKey(nextSections, field.key));
    if (lostFields.length > 0) {
      const { sections: synthesized } = synthesizePreselectSections(fallback.schemaFields, { fields: lostFields }, fallback.ctx);
      synthesized.forEach(section => {
        injectedCount += section.items.length;
        fallbackSections.push({
          ...section,
          items: section.items.map(item => ({ ...item, preselectInjected: true })),
        });
      });
    }
  }

  // A field the fallback recovered is applied, not dropped — reporting both
  // makes the "unresolved" diagnostic lie about filters that are in effect.
  // Unknown/DateTime/excluded keys have no fallback section, so they stay.
  return {
    sections: [...nextSections, ...fallbackSections],
    appliedCount,
    injectedCount,
    dropped: dropped.filter(key => !findSectionByKey(fallbackSections, key)),
  };
};
